import { useEffect, useMemo } from 'react';

import { mergeBankDossiers } from '../../../api/workOrders/bankDossier';
import {
  GetWorkOrdersParams,
  WorkOrderData,
} from '../../../api/workOrders/types';
import {
  dedupeWorkOrdersById,
  flattenWorkOrders,
} from '../../../api/workOrders/utils';
import { useBankDossierLookup } from '../../../hooks/useBankDossierLookup';
import useErrorMessage from '../../../hooks/useErrorMessage';
import useSnackbar from '../../../hooks/useSnackbar';
import { applyClientSideFilters } from './extendedFilters';
import {
  buildParams,
  hasServerFilters,
  invalidateWorkOrders,
  usePipelineWorkOrdersQuery,
  useWorkOrdersListQuery,
} from './queries';

interface UseWorkOrdersFlatArgs {
  enabled: boolean;
  searchField: string;
  filters: GetWorkOrdersParams | undefined;
}

export interface UseWorkOrdersFlatReturn {
  data: WorkOrderData[];
  loading: boolean;
  loadingMore: boolean;
  hasMore: boolean;
  loadMore: () => void;
  refetch: () => void;
}

/**
 * Flat list for the table, map and gallery views.
 *
 * Without server-side filters it pages `/work-orders/list` (50 per page,
 * "carregar mais" fetches the next page). With UF/city/company/date filters
 * it falls back to the pipeline endpoint, the only one that accepts them, and
 * flattens its status buckets. Client-side filters (status, SLA, report
 * type) apply on top in both cases. Bank dossiers are merged in when their
 * batch arrives.
 */
export function useWorkOrdersFlat({
  enabled,
  searchField,
  filters,
}: UseWorkOrdersFlatArgs): UseWorkOrdersFlatReturn {
  const { handleSnackbar } = useSnackbar();
  const { getErrorMessage } = useErrorMessage();

  const serverFiltered = hasServerFilters(filters);
  const params = useMemo(
    () => buildParams(searchField, filters),
    [searchField, filters]
  );

  const listQuery = useWorkOrdersListQuery(
    searchField,
    enabled && !serverFiltered
  );
  const pipelineQuery = usePipelineWorkOrdersQuery(
    params,
    enabled && serverFiltered
  );

  const error = serverFiltered ? pipelineQuery.error : listQuery.error;
  useEffect(() => {
    if (error) handleSnackbar(getErrorMessage(error), true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error]);

  // One page per control center response: each gets its own dossier batch.
  const pages = useMemo(
    () =>
      serverFiltered
        ? [flattenWorkOrders(pipelineQuery.data?.data)]
        : (listQuery.data?.pages ?? []).map((page) => page.data ?? []),
    [serverFiltered, pipelineQuery.data, listQuery.data]
  );
  const idPages = useMemo(
    () =>
      pages
        .map((rows) => rows.map((row) => row.id))
        .filter((ids) => ids.length > 0),
    [pages]
  );
  const dossiers = useBankDossierLookup(idPages);

  const data = useMemo(() => {
    const rows = mergeBankDossiers(pages.flat(), dossiers);
    return applyClientSideFilters(dedupeWorkOrdersById(rows), filters);
  }, [pages, dossiers, filters]);

  return {
    data,
    loading: serverFiltered ? pipelineQuery.isLoading : listQuery.isLoading,
    loadingMore: listQuery.isFetchingNextPage,
    hasMore: !serverFiltered && Boolean(listQuery.hasNextPage),
    loadMore: () => {
      if (!serverFiltered && listQuery.hasNextPage) listQuery.fetchNextPage();
    },
    refetch: () => {
      invalidateWorkOrders();
    },
  };
}
